import { OrthographicCamera, PerspectiveCamera, Quaternion, Vector3 } from 'three';

import cameraManager from '../camera-manager';
import { CoordinateManager } from '../coordinate-manager';
import { ProjectGeodataManager } from '../project-geodata-manager';
import { SkyView } from '../skyview';
import { Position3, ReportCamera, ReportState } from './manifest';

/**
 * The viewer's camera as three.js has it, in the world: its world position with the origo added
 * and its world quaternion, and the canvas it draws on.
 */
export function collectCamera(skyview: SkyView): ReportCamera | undefined {
  const camera = cameraManager().getActiveCamera()?.getTHREECamera();
  if (!(camera instanceof PerspectiveCamera || camera instanceof OrthographicCamera)) {
    return undefined;
  }

  const origo = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();
  camera.updateWorldMatrix(true, false);
  const canvas = skyview.renderer?.domElement;
  const common = {
    position: camera.getWorldPosition(new Vector3()).add(origo).toArray() as Position3,
    rotation: camera.getWorldQuaternion(new Quaternion()).toArray() as ReportCamera['rotation'],
    near: camera.near,
    far: camera.far,
    ...(canvas && canvas.height > 0
      ? { aspect: canvas.width / canvas.height, canvasHeightPx: canvas.height }
      : {}),
  };

  return camera instanceof PerspectiveCamera
    ? { type: 'perspective', ...common, fov: camera.fov }
    : {
        type: 'orthographic',
        ...common,
        zoom: camera.zoom,
        frustum: {
          left: camera.left,
          right: camera.right,
          top: camera.top,
          bottom: camera.bottom,
        },
      };
}

/**
 * What every view's state has: the timeline container shown, the camera and the occlusion culling.
 */
export function collectViewerState(
  skyview: SkyView,
): Pick<ReportState, 'timeline' | 'camera' | 'occlusionCulling'> {
  const containerId = skyview.isCadViewerActive()
    ? undefined
    : ProjectGeodataManager.instance().getActiveTimelineContainer()?.id;
  return {
    ...(containerId ? { timeline: { containerId } } : {}),
    camera: collectCamera(skyview),
    occlusionCulling: skyview.getOcclusionCulling(),
  };
}
